import { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { getOfficeLayout } from '@features/desks/services/DeskBooking';

// What a read-only floor plan needs to draw itself. Matches the floor plan shape
// served by /api/signage/floor-plan and the occupancy heat map report.
export interface FloorPlanSource {
  floorId: number;
  officePlan: string | null;
  officeFloorPlan: string | null;
  desks: {
    id: number;
    label: string;
    x: number | null;
    y: number | null;
    width: number | null;
    height: number | null;
    rotation: number | null;
    active: boolean;
  }[];
}

export interface PositionedDesk {
  deskId: number;
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
}

export interface LegacyLayout {
  floorPlan: string;
  width?: number;
  height?: number;
  desks?: { deskId: number; label?: string; x: number; y: number; width?: number; height?: number; rotate?: number }[];
  areas?: { label: string; x: number; y: number; width?: number; height?: number }[];
}

export const DEFAULT_DESK_SIZE = 32;

export function floorPlanImageUrl(path: string): string {
  if (path.startsWith('s3/')) {
    return `https://team-today-floorplans.s3.eu-west-2.amazonaws.com/${path}`;
  }
  return path;
}

/**
 * Loads a floor's plan image (inline SVG, or the legacy JSON layout it points to) and places
 * its desks, DB coordinates first with the legacy layout filling in for older desks.
 */
export function useFloorPlanLayout(floorPlan: FloorPlanSource) {
  const [imageSize, setImageSize] = useState<{ w: number; h: number } | null>(null);
  const [legacyLayout, setLegacyLayout] = useState<LegacyLayout | null>(null);

  const usesInlineSvg = !!floorPlan.officeFloorPlan;

  useEffect(() => {
    setLegacyLayout(null);
    setImageSize(null);
    if (!usesInlineSvg && floorPlan.officePlan) {
      let cancelled = false;
      getOfficeLayout(floorPlan.officePlan)
        .then(layout => { if (!cancelled) setLegacyLayout(layout); })
        .catch(() => { /* keep showing nothing rather than crash the page */ });
      return () => { cancelled = true; };
    }
  }, [floorPlan.floorId, floorPlan.officePlan, usesInlineSvg]);

  const imageSrc = useMemo(() => {
    if (usesInlineSvg && floorPlan.officeFloorPlan) {
      return 'data:image/svg+xml,' + encodeURIComponent(floorPlan.officeFloorPlan);
    }
    if (legacyLayout?.floorPlan) {
      return floorPlanImageUrl(legacyLayout.floorPlan);
    }
    return null;
  }, [usesInlineSvg, floorPlan.officeFloorPlan, legacyLayout]);

  // DB coordinates win; legacy plan JSON fills in for desks that predate them.
  const desks: PositionedDesk[] = useMemo(() => {
    const legacyByDeskId = new Map<number, { x: number; y: number; width?: number; height?: number; rotate?: number; label?: string }>();
    (legacyLayout?.desks ?? []).forEach(d => legacyByDeskId.set(d.deskId, d));

    return floorPlan.desks
      .filter(d => d.active)
      .map(d => {
        const legacy = legacyByDeskId.get(d.id);
        const x = d.x ?? legacy?.x;
        const y = d.y ?? legacy?.y;
        if (x === undefined || x === null || y === undefined || y === null) return null;
        return {
          deskId: d.id,
          label: d.label,
          x: Number(x),
          y: Number(y),
          width: Number(d.width ?? legacy?.width ?? DEFAULT_DESK_SIZE),
          height: Number(d.height ?? legacy?.height ?? DEFAULT_DESK_SIZE),
          rotation: Number(d.rotation ?? legacy?.rotate ?? 0),
        };
      })
      .filter((d): d is PositionedDesk => d !== null);
  }, [floorPlan.desks, legacyLayout]);

  const onImageLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    if (img.naturalWidth && img.naturalHeight) {
      setImageSize({ w: img.naturalWidth, h: img.naturalHeight });
    }
  };

  return {
    imageSrc,
    legacyLayout,
    desks,
    planWidth: imageSize?.w ?? legacyLayout?.width ?? null,
    planHeight: imageSize?.h ?? legacyLayout?.height ?? null,
    onImageLoad,
  };
}
